import handleError from '@fuse/core/errorHandler';
import { Button, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCreateLangMutation, useUpdateLangMutation } from 'src/app/services/spApi';

const ALLOWED_PATTERN = /^[\p{L}\p{N}]/u;

const defaultValues = {
  id: 0,
  code: '',
  nameRu: '',
  nameUz: '',
};

const TranslationsForm = ({ translation, onClose, refetchList }) => {
  const { t } = useTranslation('translations');

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: translation ?? defaultValues,
  });

  const [create, { isLoading: isCreateLoading }] = useCreateLangMutation();
  const [update, { isLoading: isUpdateLoading }] = useUpdateLangMutation();

  const handleClose = () => {
    onClose?.();
    reset(defaultValues);
  };

  const trimFormValues = (data) =>
    Object.keys(data).reduce((acc, key) => {
      acc[key] = typeof data[key] === 'string' ? data[key].trim() : data[key];
      return acc;
    }, {});

  const onSubmit = async (data) => {
    const trimmedData = trimFormValues(data);
    const promise = translation ? update(trimmedData) : create(trimmedData);
    promise
      .unwrap()
      .then(() => {
        refetchList?.();
        handleClose();
      })
      .catch((error) => {
        handleError({ response: error });
      });
  };

  const makeRules = () => ({
    required: t('RequiredField') || true,
    validate: (value) => {
      const v = (value ?? '').trim();

      if (!v) {
        return t('EmptyInput') || 'This field cannot be empty';
      }

      if (!ALLOWED_PATTERN.test(v)) {
        return t('SpecialCharactersNotAllowed') || 'The first character must be a letter or number';
      }

      if (!ALLOWED_PATTERN.test(v)) {
        return (
          t('SpecialCharactersNotAllowed') ||
          "Only letters, numbers, spaces and - . , _ ( ) / & : + ' are allowed"
        );
      }
      return true;
    },
  });

  const onChangeTrimStart = (fieldOnChange) => (e) => {
    const val = e.target.value.trimStart();
    fieldOnChange(val);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="pt-10">
      <div className="flex flex-col gap-16 max-w-[1000px] mb-10">
        <Controller
          name="code"
          control={control}
          rules={makeRules()}
          render={({ field }) => (
            <TextField
              className="w-sm"
              {...field}
              fullWidth
              label={t('Code')}
              error={!!errors.code}
              helperText={errors.code?.message}
              variant="outlined"
              required
              onChange={onChangeTrimStart(field.onChange)}
            />
          )}
        />
        <Controller
          name="nameRu"
          control={control}
          rules={makeRules()}
          render={({ field }) => (
            <TextField
              className="w-sm"
              {...field}
              fullWidth
              label={`${t('Title')} (рус.)`}
              error={!!errors.nameRu}
              helperText={errors.nameRu?.message}
              variant="outlined"
              required
              onChange={onChangeTrimStart(field.onChange)}
            />
          )}
        />
        <Controller
          name="nameUz"
          control={control}
          rules={makeRules()}
          render={({ field }) => (
            <TextField
              className="w-sm"
              {...field}
              fullWidth
              label={`${t('Title')} (лат.)`}
              error={!!errors.nameUz}
              helperText={errors.nameUz?.message}
              variant="outlined"
              required
              onChange={onChangeTrimStart(field.onChange)}
            />
          )}
        />
      </div>
      <Button
        disabled={isCreateLoading || isUpdateLoading || !isValid}
        type="submit"
        className="w-full"
        variant="contained"
        color="secondary"
      >
        <span className="mx-8">{t('Save')}</span>
      </Button>
    </form>
  );
};

export default TranslationsForm;
